import React, { useCallback, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { ConversationListSkeleton } from "./InboxSkeletons";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import StarIcon from "@mui/icons-material/Star";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ChannelBadge from "./ChannelBadge";

function formatRelativeTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function statusColor(status) {
  if (status === "open") return "#22c55e";
  if (status === "pending") return "#f59e0b";
  if (status === "snoozed") return "#8b5cf6";
  return "#94a3b8";
}

function avatarColor(text) {
  const colors = ["#00a884", "#53bdeb", "#7f66ff", "#ff7eb3", "#ffa348", "#25d366", "#02735e", "#5b72e0"];
  let hash = 0;
  for (let i = 0; i < (text || "").length; i++) hash = text.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const ConversationItem = React.memo(function ConversationItem({ conv, active, onClick }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
      style={{
        display: "flex",
        gap: 10,
        padding: "10px 14px",
        cursor: "pointer",
        background: active ? "#ede9fe" : "transparent",
        borderLeft: active ? "3px solid #7c3aed" : "3px solid transparent",
        transition: "background 0.15s",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: avatarColor(conv.display_name || conv.contact_identifier),
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {initials(conv.display_name || conv.contact_identifier)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {conv.display_name || conv.contact_identifier}
          </span>
          <span style={{ fontSize: 11, color: "#94a3b8", flexShrink: 0, marginLeft: 4 }}>
            {formatRelativeTime(conv.last_message_at)}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
          {conv.last_channel && <ChannelBadge channel={conv.last_channel} size={13} />}
          <span
            style={{
              fontSize: 12,
              color: "#64748b",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {conv.last_message_preview || "Tap to view"}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              padding: "1px 6px",
              borderRadius: 4,
              background: statusColor(conv.status) + "22",
              color: statusColor(conv.status),
              textTransform: "capitalize",
            }}
          >
            {conv.status}
          </span>
          {conv.unread_count > 0 && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: "1px 6px",
                borderRadius: 10,
                background: "#7c3aed",
                color: "#fff",
              }}
            >
              {conv.unread_count}
            </span>
          )}
          {conv.is_starred && <StarIcon style={{ fontSize: 14, color: "#f59e0b" }} />}
        </div>
      </div>
    </div>
  );
});

export default function ConversationList({
  conversations,
  isLoading,
  activeId,
  onSelect,
  statusFilter,
  onStatusFilter,
  channelFilter,
  onChannelFilter,
  searchTerm,
  onSearch,
  /** When true, list sits on the right (border separates from chat on the left). */
  isRightColumn = false,
}) {
  const edgeBorder = isRightColumn
    ? { borderLeft: "1px solid #e2e8f0" }
    : { borderRight: "1px solid #e2e8f0" };
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", ...edgeBorder }}>
      <div style={{ padding: "12px 14px 8px", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <ForumOutlinedIcon sx={{ fontSize: 18, color: "#64748b" }} />
          <span style={{ fontWeight: 700, fontSize: 15 }}>Conversations</span>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            background: "#f1f5f9",
            borderRadius: 8,
            padding: "4px 8px",
            marginBottom: 6,
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
          <input
            type="text"
            placeholder="Search name or number"
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
            style={{
              border: "none",
              outline: "none",
              background: "transparent",
              flex: 1,
              fontSize: 13,
              padding: "3px 0",
            }}
          />
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilter(e.target.value)}
            style={{ flex: 1, fontSize: 12, padding: "3px 4px", borderRadius: 6, border: "1px solid #e2e8f0" }}
          >
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="closed">Closed</option>
            <option value="snoozed">Snoozed</option>
          </select>
          <select
            value={channelFilter}
            onChange={(e) => onChannelFilter(e.target.value)}
            style={{ flex: 1, fontSize: 12, padding: "3px 4px", borderRadius: 6, border: "1px solid #e2e8f0" }}
          >
            <option value="">All channels</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto" }}>
        {isLoading ? (
          <ConversationListSkeleton rows={9} />
        ) : conversations.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 16px", color: "#94a3b8" }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 44 }} />
            <div style={{ marginTop: 8, fontWeight: 600 }}>No conversations</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Incoming messages will appear here.</div>
          </div>
        ) : (
          conversations.map((c) => (
            <ConversationItem
              key={c.id}
              conv={c}
              active={c.id === activeId}
              onClick={() => onSelect(c.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}
